import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import { Disclosure } from '@common/elements/Disclosure';
import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

type UIProps = {
  prevPage?: string | (() => void);
  nextPage?: string | (() => void);
};

export const UI: React.FC<PropsWithChildren<UIProps>> = ({ prevPage, nextPage, children }) => {
  const navigate = useNavigate();
  const [showDisclosure, setShowDisclosure] = React.useState(false);

  const handlePrevPage = () => {
    if (typeof prevPage === 'string') return navigate(prevPage);
    if (typeof prevPage === 'function') return prevPage();
  };

  const handleNextPage = () => {
    if (typeof nextPage === 'string') return navigate(nextPage);
    if (typeof nextPage === 'function') return nextPage();
  };

  return (
    <Flex bg="white" direction="column" minHeight={'100vh'} w="full" gap={[0, 50]} pb={10}>
      {/* Disclosure modal */}
      <Modal isOpen={showDisclosure} onClose={() => setShowDisclosure(false)} size={'4xl'} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Important Disclosures</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Disclosure />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={() => setShowDisclosure(false)} variant="outline">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Navigation */}
      <Flex bg="white" justify={'center'} borderBottomWidth={1} borderBottomColor={'gray.100'}>
        <Flex
          maxW={920}
          mx={[5, 20]}
          width="full"
          direction="row"
          height={[70, 90]}
          color="white"
          alignItems={'center'}
          justifyContent="space-between">
          <Flex alignItems="flex-start">
            <Box as="button" type="button" onClick={handlePrevPage} w="30px">
              {prevPage && <Icon color="black" boxSize={['20px', '25px']} icon="arrow-left-long" />}
            </Box>
          </Flex>
          <Flex>
            <Image image="logo-black" w={[100, 160]} />
          </Flex>
          <Flex alignItems="flex-start">
            <Box as="button" type="button" onClick={handleNextPage} w="30px">
              {nextPage && <Icon color="black" boxSize={['20px', '25px']} icon="arrow-right-long" />}
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {/* Body */}
      <Flex alignItems="center" direction={'column'} gap={5} mx={[5, 5, 20]} my={[0, 5]}>
        {/* Content */}
        <Flex marginX={'auto'} maxWidth={920} width="full">
          <Flex
            bg="white"
            borderRight={10}
            borderRadius={15}
            boxShadow={['none', '0px 2px 10px rgba(0, 0, 0, 0.15)']}
            direction="column"
            justifyContent="space-betweeen"
            width="full"
            minH={100}>
            {children}
          </Flex>
        </Flex>

        {/* Footer */}
        <Flex paddingX={5} direction={['column', 'row']} maxWidth={920} w="full" gap={1} align={['center', 'start']}>
          <Flex flex={['auto', 1]}>
            <Link fontSize={'xs'} fontWeight="bold" onClick={() => setShowDisclosure(true)}>
              Disclosure
            </Link>
          </Flex>
          <Flex flex={['auto', 2]} justify={['end', 'start']} textAlign={['right', 'left']} direction="row" gap={2}>
            <Icon boxSize={3} color="gray.300" icon="lock"></Icon>
            <TextBlock color="gray.400" fontSize={12}>
              Your info is secure and will never be shared
            </TextBlock>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
